import { handleActions } from 'redux-actions';
import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import _get from 'lodash/get';

import moment from 'moment';
import qs from 'qs';

import checkIsExcludedCountryCode from '../shared/checkIsExcludedCountryCode';
import http from '../shared/http';
import endpoints from '../../config/endpoints';

import intl from '../shared/intl';
import checkPermissions from '../shared/check-permissions';

import { default as createCalendar } from '../shared/calendar';
import createFilters from '../shared/filters';

const isRocket = process.env.CPA_ENV === 'rocket';

const calendarOfCreationDate = createCalendar({
	namespace: 'dailyApprovesList',
	stateNodeName: 'calendarOfCreationDate',
	defaultValue: {
		start: moment().subtract(30, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

// actions types
const FETCH_DATA_SUCCESS = 'daily-approves-list/fetch-data-success';
const FETCH_DATA_ERROR = 'daily-approves-list/fetch-data-error';

const TOGGLE_PROGRESS_BAR = 'daily-approves-list/toggle-progress-bar';

const LOOKUP_SUCCESS = 'daily-approves-list/lookup-success';
const LOOKUP_ERROR = 'daily-approves-list/lookup-error';

const UPDATE_COMMENT_SUCCESS = 'daily-approves-list/request-manual-approve-success';
const UPDATE_COMMENT_ERROR = 'daily-approves-list/request-manual-approve-error';

const SORT_BY_COLUMN = 'daily-approves-list/table-sort-by-column';

// permissions
export const allowedPermissions = {
	view: 'conversions_approve_daily'
};

const filters = createFilters({
	namespace: 'dailyApprovesList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'locations_ids',
			title: 'Location',
			placeholder: 'Add location',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC
			}
		},
		{
			name: 'offer_ids',
			title: 'Offers',
			placeholder: 'Add offer',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'affiliate_id',
			title: 'Affiliates',
			placeholder: 'Add affiliate',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATES
			}
		},
		{
			name: 'admin_ids',
			title: 'Managers',
			placeholder: 'Add Manager',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS
			}
		},
		{
			name: 'status',
			title: 'Status',
			placeholder: 'Add status',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.daily-approve-statuses'
			}
		}]
});

// initial state
const initialState = {
	data: null,
	calendarOfCreationDate: calendarOfCreationDate.initialState,
	manualApproveInitialValues: {
		affiliates: null,
		offers: null,
		countries: null,
		percent_to_approve: null
	},
	table: {
		columns: {
			manager: {
				index: 'manager',
				title: 'Менеджер',
				className: 'fixed-col'
			},
			affiliate: {
				index: 'affiliate',
				title: 'Веб',
				className: 'table-cell sort-header active desc'
			},
			offer: {
				index: 'offer',
				title: 'Оффер',
				className: 'table-cell'
			},
			country: {
				index: 'country',
				title: 'Страна',
				className: 'table-cell'
			},
			sid: {
				index: 'sid',
				title: 'SID',
				className: 'table-cell'
			},
			approve_percent: {
				index: 'approve_percent',
				title: '%%аппрува',
				className: 'table-cell'
			},
			minimum_leads_count: {
				index: 'minimum_leads_count',
				title: 'Min leads',
				className: 'table-cell'
			},
			start_leads_count: {
				index: 'start_leads_count',
				title: 'Start leads',
				className: 'table-cell'
			},
			created_at: {
				index: 'created_at',
				title: 'Дата включения',
				className: 'table-cell'
			},
			removed_at: {
				index: 'removed_at',
				title: 'Дата отключения',
				className: 'table-cell'
			},
			status: {
				index: 'status',
				title: 'Статус',
				className: 'table-cell'
			},
			is_on_period: {
				index: 'is_on_period',
				title: 'Включен на период',
				className: 'table-cell'
			},
			t80_hours: {
				index: 't80_hours',
				title: 'T80 (hours)',
				className: 'table-cell'
			},
			past_valid_leads_count: {
				index: 'past_valid_leads_count',
				title: '7 days valid',
				className: 'table-cell'
			},
			comment: {
				index: 'comment',
				title: 'Коммент',
				className: 'fixed-col-right table-cell'
			}
		}
	},
	sortByColumn: {
		index: 'created_at',
		direction: 'desc'
	},
	filters: filters.initialState,
	isRequestForApprovePopupVisible: false,
	showProgressBar: false,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => (dispatch, getState) => {
			request( getState)(dispatch);
		},

		locationsLookup: text => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.LOCATIONS}?query=${text}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					const data = action.payload.data
						.filter(entity => !(isRocket && checkIsExcludedCountryCode(entity.country_code)));

					return { options: data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},

		affiliatesLookup: text => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		sortByColumn: columnIndex => (dispatch, getState) => {
			const { sortedColumn } =  selectors.getTable(getState());
			let direction = 'desc';

			if (sortedColumn.index === columnIndex && sortedColumn.direction === 'desc') {
				direction = 'asc';
			}

			dispatch({ type: SORT_BY_COLUMN, payload: { index: columnIndex, direction  } });
			dispatch(actions.fetchData({}));
		},
		updateComment: (id, formData) => (dispatch) => {
			const comment = _get(formData, 'comment');

			const approveRequest = http({
				url: `${endpoints.DAILY_APPROVES.BASE}/${id}/comment`,
				method: 'put',
				data: { comment },
				actions: [UPDATE_COMMENT_SUCCESS, UPDATE_COMMENT_ERROR]
			});

			return approveRequest(dispatch)
				.then(() => dispatch(actions.fetchData()));
		},
		offersLookup: text => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.OFFERS}?${qs.stringify({ query: text, isShowedRemoved: true })}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		})
	},
	calendarOfCreationDate.actions,
	filters.actions
);

// reducers
const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data,
			showProgressBar: false,
			error: false
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[SORT_BY_COLUMN]: (state, action) => ({ ...state, sortByColumn: action.payload }),
		[LOOKUP_ERROR]: state => ({ ...state, showProgressBar: false, error: true }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar })
	},
	calendarOfCreationDate.reducers,
	filters.reducers
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.dailyApprovesList.data,
	getCalendarOfCreationDate: state => calendarOfCreationDate.selectors
		.getCalendar(state => state.dailyApprovesList.calendarOfCreationDate, state => state)(state),
	getTable: state => {
		const { table, data, sortByColumn } = state.dailyApprovesList;
		const rows = state.dailyApprovesList.data ? data : [];
		return {
			headers: values(table.columns),
			rows: rows.slice(0, table.rowsNumber),
			sortedColumn: sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: rows.length
		};
	},
	getFilters: state => filters.selectors.getFilters(
		state => state.dailyApprovesList.filters,
		state => state
	)(state),
	getManualApproveInitialValues: state => state.dailyApprovesList.manualApproveInitialValues,
	getIntl: state => intl(state),
	getProgressBar: state => state.dailyApprovesList.showProgressBar,
	getIsRequestForApprovePopupVisible: state => state.dailyApprovesList.isRequestForApprovePopupVisible,
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.dailyApprovesList.error
};

const request = (getState) => {
	const query = {};

	const calendar = selectors.getCalendarOfCreationDate(getState()).value;
	const selectedFilters = selectors.getFilters(getState()).selectedFilters;
	const { sortedColumn } = selectors.getTable(getState());
	query.sort = { ...sortedColumn };

	for (const filter of selectedFilters) {
		if (!filter.value || !filter.value[0] || !filter.name) {
			continue;
		}
		query.filters = query.filters || {};
		query.filters[filter.name]  = filter.value.map(val => val.value);
	}
	query.date_range = { start: `${calendar.start}T00:00:00Z`, end: `${calendar.end}T23:59:59Z` };

	return http({
		url: `${endpoints.DAILY_APPROVES.BASE}?${qs.stringify(query)}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	});
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/daily-approves-list/index.js